import {CabinetMesh} from 'components/customer/RoomPlanner/types';

/**
 * Same ids as cabinets.style / PlaceholderFace STYLES: Base sits on the floor,
 * Upper hangs on the wall.
 */
export const PLACEHOLDER_STYLE_BASE = 1;
export const PLACEHOLDER_STYLE_UPPER = 3;

type PlaceholderStyleUserData = {
    isPlaceholder?: boolean;
    isUpperProduct?: boolean;
    placeholderStyle?: number;
    placeholderCategory?: string;
};

const readPlaceholderCategory = (
    groupOrCategory: CabinetMesh | string | null | undefined
): string => {
    if (typeof groupOrCategory === 'string') return groupOrCategory;
    const userData = groupOrCategory?.userData as
        PlaceholderStyleUserData | undefined;
    return userData?.placeholderCategory || '';
};

/**
 * True when this placeholder is a rangehood (category from the admin face).
 * Used only as a hang fallback when `style` was never stored.
 * @param {CabinetMesh | string | null | undefined} groupOrCategory Mesh or category name
 * @return {boolean}
 */
export const isRangehoodPlaceholder = (
    groupOrCategory: CabinetMesh | string | null | undefined
): boolean =>
    readPlaceholderCategory(groupOrCategory).trim().toLowerCase() ===
    'rangehood';

const hasExplicitStyle = (style: unknown): boolean =>
    style != null &&
    String(style).trim() !== '' &&
    Number.isFinite(Number(style));

/**
 * True when the catalogue style is Upper (cabinets.style = 3).
 * @param {unknown} style Face `style` from the API
 * @return {boolean}
 */
export const isUpperPlaceholderStyle = (style: unknown): boolean =>
    Number(style) === PLACEHOLDER_STYLE_UPPER;

/**
 * Resolve Base vs Upper. An explicit `style` wins; missing style still hangs
 * rangehoods so older catalogue rows keep working.
 * @param {unknown} style Face `style` (1 Base, 3 Upper)
 * @param {string | null | undefined} category Face category
 * @return {number} PLACEHOLDER_STYLE_BASE or PLACEHOLDER_STYLE_UPPER
 */
export const resolvePlaceholderStyle = (
    style?: unknown,
    category?: string | null
): number => {
    if (hasExplicitStyle(style)) {
        return isUpperPlaceholderStyle(style)
            ? PLACEHOLDER_STYLE_UPPER
            : PLACEHOLDER_STYLE_BASE;
    }
    return isRangehoodPlaceholder(category)
        ? PLACEHOLDER_STYLE_UPPER
        : PLACEHOLDER_STYLE_BASE;
};

/**
 * Whether this catalogue face should mount like a wall cabinet.
 * @param {unknown} style Face `style`
 * @param {string | null | undefined} category Face category
 * @return {boolean}
 */
export const placeholderHangsOnWall = (
    style?: unknown,
    category?: string | null
): boolean =>
    resolvePlaceholderStyle(style, category) === PLACEHOLDER_STYLE_UPPER;

/**
 * Wall-hung cabinets and upper placeholders pack independently of floor items.
 * @param {CabinetMesh} item Cabinet or placeholder
 * @return {boolean}
 */
export const isHangingLayoutItem = (item: CabinetMesh): boolean => {
    const userData = item.userData as PlaceholderStyleUserData | undefined;
    if (Boolean(userData?.isUpperProduct)) return true;
    if (!userData?.isPlaceholder) return false;
    return placeholderHangsOnWall(
        userData.placeholderStyle,
        userData.placeholderCategory
    );
};
